import { useState } from "react";
import { IconSend, IconAlertTriangle } from "@tabler/icons-react";
import { ROLE_OPTIONS } from "./data";
import Input, { Select } from "../UI/FormInput/input";
import ModalShell from "../Support/ModalShell";
import Button from "../UI/Button/button";
import { getTeamApiError } from "./utils/apiError";

const EMPTY_FORM = {
  firstName: "",
  lastName: "",
  email: "",
  role: "",
  teamId: "",
};

const rolesFor = (currentUserRole) =>
  currentUserRole === "Manager"
    ? ROLE_OPTIONS.filter((r) => r === "Member" || r === "Viewer")
    : ROLE_OPTIONS;

export default function AddUserModal({
  open,
  onClose,
  onAddUser,
  teams,
  currentUserRole,
}) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const setField = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleClose = () => {
    setForm(EMPTY_FORM);
    setError(null);
    onClose();
  };

  const handleSubmit = async () => {
    if (
      !form.firstName.trim() ||
      !form.lastName.trim() ||
      !form.email.trim() ||
      !form.role
    ) {
      setError("Please fill in all required fields.");
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await onAddUser({
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email.trim(),
        role: form.role,
        teamId: form.teamId || undefined,
      });
      setForm(EMPTY_FORM);
    } catch (err) {
      setError(getTeamApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell
      open={open}
      onClose={handleClose}
      title="Invite New User"
      footer={
        <>
          <Button
            variant="outlineBlue"
            onClick={handleClose}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            leftIcon={<IconSend size={15} />}
            onClick={handleSubmit}
            loading={submitting}
          >
            Send Invite
          </Button>
        </>
      }
    >
      {error && (
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 8,
            background: "#fef2f2",
            border: "1px solid #fecaca",
            color: "#b91c1c",
            borderRadius: 8,
            padding: "10px 12px",
            fontSize: 13,
            marginBottom: 12,
          }}
        >
          <IconAlertTriangle
            size={15}
            style={{ flexShrink: 0, marginTop: 1 }}
          />
          <span>
            {typeof error === "string"
              ? error
              : "Please check the form and try again."}
          </span>
        </div>
      )}

      <Input
        label="First Name"
        required
        id="newUserFirstName"
        placeholder="e.g. Jane"
        value={form.firstName}
        onChange={setField("firstName")}
      />
      <Input
        label="Last Name"
        required
        id="newUserLastName"
        placeholder="e.g. Doe"
        value={form.lastName}
        onChange={setField("lastName")}
      />
      <Input
        label="Email Address"
        required
        id="newUserEmail"
        type="email"
        placeholder="jane@company.com"
        hint="An invitation will be sent to this address."
        value={form.email}
        onChange={setField("email")}
      />
      <Select
        label="Role"
        required
        id="newUserRole"
        value={form.role}
        onChange={setField("role")}
      >
        <option value="">Select role…</option>
        {rolesFor(currentUserRole)?.map((role) => (
          <option key={role} value={role}>
            {role}
          </option>
        ))}
      </Select>
      <Select
        label="Assign to Team"
        id="newUserTeam"
        value={form.teamId}
        onChange={setField("teamId")}
      >
        <option value="">No team (assign later)</option>
        {teams?.map((team) => (
          <option key={team?._id} value={team?._id}>
            {team?.name}
          </option>
        ))}
      </Select>
    </ModalShell>
  );
}
